import { useEffect, useState } from "react";
import apiClient, { formatApiError, mediaUrl } from "../api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import MediaPicker from "../components/MediaPicker";
import { Plus, ChevronUp, ChevronDown, Eye, EyeOff, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

const BLOCK_TYPES = [
  { value: "hero", label: "Hero" },
  { value: "text", label: "Tekst" },
  { value: "image", label: "Afbeelding" },
  { value: "text_image", label: "Tekst + afbeelding" },
  { value: "quote", label: "Citaat" },
  { value: "cta", label: "Call-to-action" },
  { value: "gallery", label: "Galerij" },
  { value: "story_highlight", label: "Story highlight" },
  { value: "service_block", label: "Service blok" },
];

const BlocksPage = () => {
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // block being edited
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/pages").then(({ data }) => {
      setPages(data);
      if (data.length && !pageId) setPageId(data[0].id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!pageId) return;
    loadBlocks();
  }, [pageId]); // eslint-disable-line

  const loadBlocks = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/blocks", {
        params: { page_id: pageId },
      });
      setBlocks(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const move = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    const ordered = [...blocks];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(newIndex, 0, moved);
    setBlocks(ordered); // optimistic
    try {
      await apiClient.post("/admin/blocks/reorder", {
        block_ids: ordered.map((b) => b.id),
      });
    } catch (e) {
      toast.error(formatApiError(e));
      loadBlocks();
    }
  };

  const toggleVisible = async (block) => {
    try {
      await apiClient.patch(`/admin/blocks/${block.id}`, {
        is_visible: !block.is_visible,
      });
      loadBlocks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (block) => {
    if (!window.confirm(`Blok "${block.title || block.type}" verwijderen?`))
      return;
    try {
      await apiClient.delete(`/admin/blocks/${block.id}`);
      toast.success("Blok verwijderd");
      loadBlocks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSaveBlock = async (block, isNew) => {
    try {
      if (isNew) {
        await apiClient.post("/admin/blocks", { ...block, page_id: pageId });
        toast.success("Blok aangemaakt");
        setCreating(false);
      } else {
        await apiClient.patch(`/admin/blocks/${block.id}`, block);
        toast.success("Blok opgeslagen");
        setEditing(null);
      }
      loadBlocks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const currentPage = pages.find((p) => p.id === pageId);

  return (
    <div data-testid="admin-blocks-page">
      <div className="mb-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
          CMS · Contentblokken
        </p>
        <h1 className="font-display text-5xl text-white tracking-tight">
          Contentblokken
        </h1>
        <p className="mt-3 text-sm text-zinc-400 font-body max-w-xl">
          Bouw flexibele paginainhoud — herschik via de pijltjes, verberg of
          verwijder blokken zonder code aan te passen.
        </p>
      </div>

      <div className="bg-ink-900 border border-white/[0.06] p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body mb-2 block">
              Selecteer pagina
            </label>
            <Select value={pageId} onValueChange={setPageId}>
              <SelectTrigger
                data-testid="blocks-page-select"
                className="h-11 bg-ink-950 border-white/10 text-sm font-body text-white rounded-none"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-ink-900 border-white/10 rounded-none">
                {pages.map((p) => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    className="text-sm font-body rounded-none"
                  >
                    {p.title} ({p.key})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="blocks-add"
            className="inline-flex items-center gap-2 px-5 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
          >
            <Plus size={13} /> Blok toevoegen
          </button>
        </div>
      </div>

      {creating && (
        <BlockForm
          initial={{
            type: "text",
            title: "",
            subtitle: "",
            body: "",
            image_id: null,
            button_text: "",
            button_link: "",
            is_visible: true,
            sort_order: 0,
          }}
          onCancel={() => setCreating(false)}
          onSave={(b) => onSaveBlock(b, true)}
          isNew
        />
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Laden...</div>
      ) : blocks.length === 0 ? (
        <div className="bg-ink-900 border border-white/[0.06] p-10 text-center text-sm text-zinc-500 font-body">
          Geen blokken op {currentPage?.title || "deze pagina"}. Voeg er één
          toe om te beginnen.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {blocks.map((block, i) => (
            <div
              key={block.id}
              data-testid={`block-row-${block.id}`}
              className={`bg-ink-900 border border-white/[0.06] ${
                editing?.id === block.id ? "border-champagne/40" : ""
              }`}
            >
              {editing?.id === block.id ? (
                <BlockForm
                  initial={editing}
                  onCancel={() => setEditing(null)}
                  onSave={(b) => onSaveBlock(b, false)}
                />
              ) : (
                <div className="flex items-center gap-4 px-5 py-5">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      data-testid={`block-up-${block.id}`}
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-champagne hover:text-champagne text-zinc-400 disabled:opacity-30 transition-colors"
                    >
                      <ChevronUp size={13} />
                    </button>
                    <button
                      type="button"
                      data-testid={`block-down-${block.id}`}
                      onClick={() => move(i, 1)}
                      disabled={i === blocks.length - 1}
                      className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-champagne hover:text-champagne text-zinc-400 disabled:opacity-30 transition-colors"
                    >
                      <ChevronDown size={13} />
                    </button>
                  </div>
                  {block.image_id ? (
                    <BlockThumb id={block.image_id} />
                  ) : (
                    <div className="w-20 h-14 bg-ink-850 flex items-center justify-center text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-body">
                      {block.type}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-champagne font-body">
                        {BLOCK_TYPES.find((t) => t.value === block.type)?.label || block.type}
                      </span>
                      {!block.is_visible && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">
                          · verborgen
                        </span>
                      )}
                    </div>
                    <p className="font-display text-xl text-white truncate">
                      {block.title || "(geen titel)"}
                    </p>
                    {block.body && (
                      <p className="text-xs text-zinc-500 font-body truncate mt-1">
                        {block.body.slice(0, 100)}...
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`block-toggle-${block.id}`}
                      onClick={() => toggleVisible(block)}
                      className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-champagne hover:text-champagne text-zinc-300 transition-colors"
                      aria-label="Tonen/verbergen"
                    >
                      {block.is_visible ? (
                        <Eye size={13} />
                      ) : (
                        <EyeOff size={13} />
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid={`block-edit-${block.id}`}
                      onClick={() => setEditing(block)}
                      className="px-4 py-2 text-[10px] tracking-[0.3em] uppercase border border-white/10 hover:border-champagne hover:text-champagne text-zinc-300 transition-colors"
                    >
                      Bewerken
                    </button>
                    <button
                      type="button"
                      data-testid={`block-delete-${block.id}`}
                      onClick={() => onDelete(block)}
                      className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-red-500/40 hover:text-red-400 text-zinc-400 transition-colors"
                      aria-label="Verwijderen"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BlockThumb = ({ id }) => {
  const [obj, setObj] = useState(null);
  useEffect(() => {
    apiClient.get("/admin/media").then(({ data }) => {
      setObj(data.find((m) => m.id === id) || null);
    });
  }, [id]);
  if (!obj)
    return <div className="w-20 h-14 bg-ink-850" />;
  return (
    <div className="w-20 h-14 bg-ink-850 overflow-hidden">
      <img
        src={mediaUrl(obj)}
        alt={obj.alt_text || ""}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const BlockForm = ({ initial, onCancel, onSave, isNew = false }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      data-testid={isNew ? "block-form-new" : `block-form-${initial.id}`}
      className="p-7 bg-ink-900 border border-champagne/30 mb-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            Type
          </span>
          <Select
            value={form.type}
            onValueChange={(v) => set("type", v)}
          >
            <SelectTrigger className="h-11 bg-ink-950 border-white/10 text-sm font-body text-white rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-ink-900 border-white/10 rounded-none">
              {BLOCK_TYPES.map((t) => (
                <SelectItem
                  key={t.value}
                  value={t.value}
                  className="text-sm font-body rounded-none"
                >
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            Titel
          </span>
          <Input
            data-testid="block-form-title"
            value={form.title || ""}
            onChange={(e) => set("title", e.target.value)}
            className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
          />
        </label>

        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            Subtitel
          </span>
          <Input
            value={form.subtitle || ""}
            onChange={(e) => set("subtitle", e.target.value)}
            className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
          />
        </label>

        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            Tekst
          </span>
          <Textarea
            data-testid="block-form-body"
            value={form.body || ""}
            onChange={(e) => set("body", e.target.value)}
            rows={6}
            className="bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none resize-none"
          />
        </label>

        <div className="md:col-span-2">
          <MediaPicker
            label="Afbeelding (optioneel)"
            value={form.image_id}
            onChange={(id) => set("image_id", id)}
          />
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            Knoptekst
          </span>
          <Input
            value={form.button_text || ""}
            onChange={(e) => set("button_text", e.target.value)}
            className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            Knoplink
          </span>
          <Input
            value={form.button_link || ""}
            onChange={(e) => set("button_link", e.target.value)}
            className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
          />
        </label>
      </div>

      <div className="flex items-center gap-3 mt-7">
        <button
          type="button"
          data-testid="block-form-save"
          onClick={() => onSave(form)}
          className="inline-flex items-center gap-2 px-5 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
        >
          <Save size={12} /> {isNew ? "Toevoegen" : "Opslaan"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-2 px-5 py-3 border border-white/10 text-zinc-300 hover:border-champagne hover:text-champagne transition-colors text-[11px] tracking-[0.3em] uppercase font-body"
        >
          <X size={12} /> Annuleren
        </button>
      </div>
    </div>
  );
};

export default BlocksPage;
